import { usePartySearchAresMutation } from "Api/Mutations/Party/usePartySearchAresMutation";
import type { PartyDto, Region } from "Infrastructure/Api/Api";
import { useEffect, useRef } from "react";

type Props = {
  initialIdentifierNumber?: string;
  identifierNumber?: string;
  region: Region | null | undefined;
  onAresData: (aresData: PartyDto | null) => void;
  onError?: () => void;
  disabled?: boolean;
};

// The lookup is keyed by region + identifier — the same identifier must re-run against the other
// market's register (ARES vs RPO) when the user switches region.
const getLookupKey = (region: Region, identifierNumber: string) =>
  `${region}:${identifierNumber}`;

export const useAres = ({
  initialIdentifierNumber,
  identifierNumber,
  region,
  onAresData,
  onError,
  disabled,
}: Props) => {
  const lastAres = useRef<string | undefined>(
    initialIdentifierNumber != null && region != null
      ? getLookupKey(region, initialIdentifierNumber)
      : undefined,
  );
  const {
    mutate: getAres,
    isPending: isFetchingAres,
    isError,
    isSuccess,
  } = usePartySearchAresMutation({ onSuccess: onAresData, onError });
  // The last lookup that failed. Not forgotten on the failure itself — the effect would re-run the moment
  // the request settles and ask again forever (a 404 for an unknown IČO never turns into a 200). It is
  // forgotten once the user edits the identifier, so re-entering the same one tries again (the register
  // may just have been down).
  const failedAres = useRef<string | undefined>(undefined);

  useEffect(() => {
    const currentKey =
      region != null && identifierNumber != null
        ? getLookupKey(region, identifierNumber)
        : undefined;

    if (failedAres.current !== undefined && failedAres.current !== currentKey) {
      if (lastAres.current === failedAres.current) {
        lastAres.current = undefined;
      }
      failedAres.current = undefined;
    }

    if (
      !disabled &&
      region != null &&
      identifierNumber?.length === 8 &&
      !isFetchingAres &&
      lastAres.current !== getLookupKey(region, identifierNumber)
    ) {
      const lookupKey = getLookupKey(region, identifierNumber);
      getAres(
        { query: identifierNumber, region },
        {
          onError: () => {
            failedAres.current = lookupKey;
          },
        },
      );
      lastAres.current = lookupKey;
    }
  }, [getAres, identifierNumber, region, isFetchingAres, disabled]);

  return {
    isFetchingAres,
    isError,
    isSuccess,
  };
};
